import { Helmet } from 'react-helmet-async';
// @mui
import { styled } from '@mui/material/styles';
import { Container, Typography, Divider, Box, Stack, Link, Alert } from '@mui/material';
import LinkStyle from '@mui/material/Link';
// components
import Logo from '../components/logo';
// sections
import { LoginForm } from '../sections/auth/login';

import { useNavigate } from 'react-router-dom';
import config from '../config';
import i18n from '../i18n';
import { useContext, useState } from 'react';
import { AppDataContext } from '../context/Context';
import CodeAuthField from '../components/code-auth-input/CodeAuthField';
import { formatError } from '../utils/formatError';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        display: 'flex'
    }
}));

const StyledContent = styled('div')(({ theme }) => ({
    maxWidth: 480,
    margin: 'auto',
    display: 'flex',
    minHeight: '75vh',
    justifyContent: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(12, 0),
        minHeight: '100vh'
    }
}));

// ----------------------------------------------------------------------

export default function LoginPage() {
    const navigate = useNavigate();
    const context = useContext(AppDataContext);
    const { server } = context.getState();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({
        username: false,
        password: false
    });

    const [showCodeAuth, setShowCodeAuth] = useState(false);

    const handleClick = async () => {
        if (username.trim() === '') {
            if (password.trim() === '') {
                setValidationErrors((prevErrors) => ({
                    ...prevErrors,
                    username: true,
                    password: true
                }));
            } else {
                setValidationErrors((prevErrors) => ({
                    ...prevErrors,
                    username: true
                }));
            }
            return;
        }
        if (password.trim() === '') {
            setValidationErrors((prevErrors) => ({
                ...prevErrors,
                password: true
            }));
            return;
        }

        setIsLoading(true);

        let result = await server.auth.loginUser(username, password);

        if (result.error === 'OK') {
            if (result.user) {
                navigate('/app/dashboard', { replace: true });
            } else {
                // user uses 2FA, we need to ask for code
                setShowCodeAuth(true);
            }
        } else {
            setError(formatError(result));
            setIsLoading(false);
        }
    };

    const handleTwoFactorAuth = async (code) => {
        setError('');
        setIsLoading(true);
        const result = await server.totp.check(code);
        if (result.error === 'OK') {
            setIsLoading(false);
            navigate('/app/dashboard', { replace: true });
        } else {
            setIsLoading(false);
            setError(formatError(result));
        }
    };

    const title = config.title ? `Login | ${config.title}` : 'Login';

    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>

            <StyledRoot>
                <Container maxWidth="sm">
                    <Logo
                        sx={{
                            position: {
                                xs: 'relative',
                                sm: 'relative',
                                md: 'relative',
                                lg: 'fixed'
                            },
                            top: { xs: 0, sm: 0, md: 0, lg: 40 },
                            left: { xs: 0, sm: 0, md: 0, lg: 40 },
                            width: 200
                        }}
                        disabledLink
                    />

                    <StyledContent>
                        <Typography variant="h4" gutterBottom>
                            {`${i18n.t('common.login')} ${config.title}`}
                        </Typography>

                        <Divider sx={{ my: 3 }}></Divider>

                        {showCodeAuth ? (
                            <>
                                <Stack spacing={3}>
                                    <Typography variant="h5" gutterBottom>
                                        {i18n.t('auth.twoFactorAuth')}
                                    </Typography>

                                    <CodeAuthField
                                        callback={handleTwoFactorAuth}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ my: 2, cursor: 'pointer' }}
                                >
                                    <Link
                                        variant="subtitle2"
                                        underline="hover"
                                        onClick={() => setShowCodeAuth(false)}
                                    >
                                        {i18n.t('auth.backToLogin')}
                                    </Link>
                                </Stack>
                            </>
                        ) : (
                            <LoginForm
                                isLoading={isLoading}
                                username={username}
                                password={password}
                                setUsername={setUsername}
                                setPassword={setPassword}
                                handleClick={handleClick}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            />
                        )}

                        <Divider sx={{ my: 3 }}></Divider>

                        {error && <Alert severity="error">{error}</Alert>}

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column'
                            }}
                        >
                            <LinkStyle
                                mb={2}
                                component={Link}
                                to={'/docs/privacy'}
                            >
                                {i18n.t('common.privacy')}
                            </LinkStyle>
                            <LinkStyle component={Link} to={'/docs/tos'}>
                                {i18n.t('common.tos')}
                            </LinkStyle>
                        </Box>
                    </StyledContent>
                </Container>
            </StyledRoot>
        </>
    );
}
